import React, { ReactNode, useEffect } from "react";
import { useSelector } from "react-redux";

import { State } from "../../@types";
import ToggleGroupFavoriteButton from "components/shared/favoriteButtons/ToggleGroupFavoriteButton";

import classNames from "classnames";
import ToggleGroupSubscribe from "components/shared/ToggleGroupSubscribeButton";
import DropDown from "components/shared/DropDown";
import { isEmpty } from "lodash";
import ToggleStreamSubscribeItem from "components/activitystreams/ToggleStreamStubscribeItem";

interface PageTitleTypes {
  title: string;
  subtitle?: string;
  renderHeadlineTools?: (() => React.ReactNode | null) | null;
  children?: React.ReactNode;
  setDocumentTitle?: boolean;
}

/*
 * Main "dumb" PageTitle component
 * Styles are duplicated in app/views/layouts/_content.html.haml
 */
export default function PageTitle({
  title,
  subtitle,
  renderHeadlineTools,
  children,
  setDocumentTitle = true,
}: PageTitleTypes) {
  if (renderHeadlineTools)
    console.warn("Prop renderHeadlineTools on PageTitle is deprecated");

  useEffect(() => {
    if (setDocumentTitle && !window.isApp) {
      document.title = `${title} | ${Preload.current_network.name}`;
    }
  }, [title]);

  return (
    <div className={"tixxt__pagetitle"}>
      <h1>{title}</h1>
      {subtitle ? <small>{subtitle}</small> : null}
      {children}
    </div>
  );
}

/*
 * Shows either the group or stream name or the given title
 * Allows user to favorite, subscribe, subscribe rss feed or leave the current group
 * Allows user to subscribe the current stream
 */
export function ContextPageTitle({
  groupSlug,
  streamSlug,
  title: titleProp,
}: {
  groupSlug?: string;
  streamSlug?: string;
  title?: string | null;
}) {
  const group = useSelector((state: State) =>
    groupSlug ? state.groups.bySlug?.[groupSlug] : null,
  );
  const title = group?.name || titleProp;
  if (!title) return null;

  const items: ReactNode[] = [];

  if (((groupSlug && group) || streamSlug) && !Preload?.page_title) {
    if (groupSlug && group) {
      if (Preload?.current_member?.authentication_token)
        items.push(
          <DropDown.Item
            url={`/groups/${groupSlug}/activitystreams/${groupSlug}/activities.atom?auth_token=${Preload.current_member.authentication_token}`}
            text={I18n.t("composer.editor.layout.abo")}
            data={{ "data-turbo": false }}
          />,
        );

      items.push(<ToggleGroupSubscribe groupSlug={groupSlug} />);

      if (group.can?.leave)
        items.push(
          <DropDown.Item
            text={I18n.t("js.directory.group_row.leave_group")}
            url={`/groups/${groupSlug}/join`}
            data={{
              "data-method": "delete",
              "data-confirm": I18n.t(
                "js.directory.group_row.leave_group_confirmation",
              ),
            }}
            dividerTop
          />,
        );
    }

    if (
      streamSlug &&
      streamSlug !== "all" &&
      Preload.current_network.config.activitystreams.subscribe_network_streams
        .enabled
    ) {
      items.push(<ToggleStreamSubscribeItem streamSlug={streamSlug} />);
    }
  }

  return (
    <PageTitle title={title}>
      <div>
        {!isEmpty(items) && (
          <>
            <DropDown.Toggle buttonType="normal" className="text-muted" small />
            <DropDown alignment="start">{items}</DropDown>
          </>
        )}
        {groupSlug && group && (
          <ToggleGroupFavoriteButton groupSlug={groupSlug} />
        )}
      </div>
    </PageTitle>
  );
}

export function Subtitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <h2 className={classNames("uppercase font-semibold text-sm", className)}>
      {children}
    </h2>
  );
}
